<script setup>
import { ref, onMounted, nextTick, watch, computed } from "vue";

const props = defineProps({
    /**
     * Quote data with price and change info
     */
    data: {
        type: Object,
        required: true,
    },
    /**
     * Show change direction icon (triangle)
     */
    showChangeIcon: {
        type: Boolean,
        default: false,
    },
    /**
     * Include dollar sign in price display
     */
    showDollarSign: {
        type: Boolean,
        default: false,
    },
    /**
     * Layout orientation for the quote
     * @values grid, vertical
     */
    layout: {
        type: String,
        default: "grid",
        validator: value => ["grid", "vertical"].includes(value),
    },
    /**
     * Always show company long name instead of symbol in vertical layout
     */
    alwaysShowLongName: {
        type: Boolean,
        default: false,
    },
});

// Refs for checking truncation
const longNameRef = ref(null);
const isTextTruncated = ref(false);

// Function to check if text is truncated (has ellipsis)
const checkTextTruncation = () => {
    if (longNameRef.value) {
        const element = longNameRef.value;
        isTextTruncated.value = element.scrollWidth > element.clientWidth;
    }
};

// Check for truncation after mounting and when component is updated
onMounted(() => {
    nextTick(checkTextTruncation);
});

// Watch for data changes to recheck truncation
watch(
    () => props.data.longName,
    () => {
        nextTick(checkTextTruncation);
    }
);

const tooltipContent = computed(() => {
    return isTextTruncated.value ? props.data.longName : undefined;
});
</script>

<template>
    <div
        class="qmodii-quote"
        :class="{
            'qmodii-quote-vertical': layout === 'vertical',
            'qmodii-quote-grid': layout === 'grid',
        }"
    >
        <template v-if="layout === 'grid'">
            <div class="qmodii-quote-flex-row">
                <div class="qmodii-quote-left qmodii-symbol">
                    <slot name="symbol"></slot>
                </div>
                <div class="qmodii-quote-right qmodii-quote-price">
                    <template v-if="showDollarSign">$</template>{{ data.last }}
                </div>
            </div>
            <div class="qmodii-quote-flex-row">
                <div
                    ref="longNameRef"
                    class="qmodii-quote-left qmodii-quote-name"
                    v-tooltip="tooltipContent"
                >
                    <slot name="name"></slot>
                </div>
                <div
                    class="qmodii-quote-right qmodii-quote-change"
                    :class="`qmodii-quote-change-${data.changeDirection}`"
                >
                    {{ data.changeSign }}{{ data.change }} ({{ data.changeSign
                    }}{{ data.changePercent }})
                    <span class="qmodii-quote-change-icon" v-if="showChangeIcon">
                        {{ data.changeDirection === "positive" ? "▲" : "▼" }}
                    </span>
                </div>
            </div>
        </template>

        <template v-else>
            <!-- For vertical layout, conditionally show long name or symbol -->
            <div
                v-if="alwaysShowLongName"
                ref="longNameRef"
                class="qmodii-quote-name qmodii-symbol"
                v-tooltip="tooltipContent"
            >
                {{ data.longName }}
            </div>
            <div v-else class="qmodii-quote-name qmodii-symbol">
                <slot name="symbol"></slot>
            </div>
            <div class="qmodii-quote-price">
                <template v-if="showDollarSign">$</template>{{ data.last }}
            </div>
            <div class="qmodii-quote-change" :class="`qmodii-quote-change-${data.changeDirection}`">
                {{ data.changeSign }}{{ data.change }} ({{ data.changeSign
                }}{{ data.changePercent }})
                <span class="qmodii-quote-change-icon" v-if="showChangeIcon">
                    {{ data.changeDirection === "positive" ? "▲" : "▼" }}
                </span>
            </div>
        </template>
    </div>
</template>

<style lang="stylus" scoped>
.qmodii-quote
    display flex
    flex-direction column
    gap var(--qmodii-spacing-sm)

    &-grid
        width 100%

    &-vertical
        .qmodii-quote-price,
        .qmodii-quote-change,
        .qmodii-quote-name
            text-align left
            justify-content flex-start

        .qmodii-quote-name
            font-weight bold
            overflow hidden
            text-overflow ellipsis
            white-space nowrap
            max-width 90%

.qmodii-quote-flex-row
    display flex
    width 100%
    justify-content space-between
    align-items baseline

.qmodii-quote-left
    text-align left

.qmodii-quote-right
    text-align right

.qmodii-quote-name
    overflow hidden
    text-overflow ellipsis
    white-space nowrap
    max-width 50%

.qmodii-quote-price
    position relative
    display inline-flex
    align-items center

.qmodii-quote-change
    text-align right
    display flex
    flex-wrap nowrap
    justify-content flex-end

    &-positive
        color var(--qmodii-quote-positive-color, var(--qmodii-up-color))

    &-negative
        color var(--qmodii-quote-negative-color, var(--qmodii-down-color))

    &-icon
        display inline-block
        margin-left 3px

.qmodii-symbol
    font-weight bold
</style>
